import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import type { FinalizeAdjudicationResponse } from "../../hooks/useOrders";
import "./bid.scss";

const renderFinalizedList = (
  items: FinalizeAdjudicationResponse["awarded"],
  emptyLabel: string,
  t: (key: string) => string
) => {
  if (items.length === 0) {
    return <p className="bid-recap__empty">{emptyLabel}</p>;
  }
  return (
    <ul className="bid-recap__list">
      {items.map((it) => (
        <li key={it.orderId} className="bid-recap__item">
          <div>
            <div className="bid-recap__item-name">
              {it.materialName || t("messages.notAvailable")}
            </div>
            <div className="bid-recap__item-meta">
              {it.quantity != null && (
                <span>
                  {it.quantity}
                  {it.measurementUnit ? ` ${it.measurementUnit}` : ""}
                </span>
              )}
              {it.internalCode && <span> #{it.internalCode}</span>}
            </div>
          </div>
          {it.awardedSupplierName && (
            <span className="bid-recap__item-award">
              {it.awardedSupplierName}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
};

export interface FinalizeRecapModalProps {
  recap: FinalizeAdjudicationResponse | null;
  onClose: () => void;
}

/**
 * The "finalize adjudication" recap (Task 7, `BuyerBid.tsx` only — `JointBuyerBid.tsx` has no
 * equivalent action, see `BidToolbar.tsx`'s own note) — Carbon `ComposedModal` in place of the
 * pre-Carbon custom `Modal`, listing which order lines got awarded vs. returned to Created.
 */
const FinalizeRecapModal = ({ recap, onClose }: FinalizeRecapModalProps) => {
  const { t } = useTranslation();

  return (
    <ComposedModal
      open={recap !== null}
      aria-label={t("tenders.data.adjudicationRecapTitle")}
      onClose={onClose}
    >
      <ModalHeader title={t("tenders.data.adjudicationRecapTitle")} />
      <ModalBody>
        {recap && (
          <div className="bid-cancel-form">
            <p>
              {t("tenders.data.adjudicationRecapBody", {
                awarded: recap.awarded.length,
                orphan: recap.orphan.length,
              })}
            </p>
            <div>
              <h3 className="bid-tile__title">
                {t("tenders.data.adjudicationRecapAwarded", {
                  count: recap.awarded.length,
                })}
              </h3>
              {renderFinalizedList(
                recap.awarded,
                t("tenders.data.adjudicationRecapAwardedEmpty"),
                t
              )}
            </div>
            <div>
              <h3 className="bid-tile__title">
                {t("tenders.data.adjudicationRecapOrphan", {
                  count: recap.orphan.length,
                })}
              </h3>
              {renderFinalizedList(
                recap.orphan,
                t("tenders.data.adjudicationRecapOrphanEmpty"),
                t
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="primary" onClick={onClose}>
          {t("cta.close")}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default FinalizeRecapModal;
